import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import { useSettings } from './SettingsContext';
import { isPersonaAvailable } from '../utils/personaRestore.js';

const SessionContext = createContext(null);
const SESSION_STORAGE_KEY = 'etch.session';

export function SessionProvider({ children }) {
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState('disconnected'); // disconnected, connecting, connected, error
  const [codebaseContext, setCodebaseContext] = useState(null);
  const [workspacePath, setWorkspacePath] = useState(null);
  const [ckbConnected, setCkbConnected] = useState(false);
  const [ckbToolCount, setCkbToolCount] = useState(0);
  const [error, setError] = useState(null);
  const [sessionProvider, setSessionProvider] = useState(null);
  const [sessionModel, setSessionModel] = useState(null);
  const [sessionMode, setSessionMode] = useState('codebase');
  const [selectedPersona, setSelectedPersonaState] = useState(null);
  const [isRestoring, setIsRestoring] = useState(true);

  const setSelectedPersona = useCallback((persona) => {
    setSelectedPersonaState(persona);
    try {
      if (persona) {
        localStorage.setItem('etch.persona', JSON.stringify({
          circleId: persona.circleId,
          circleName: persona.circleName,
          personaId: persona.personaId,
          name: persona.name,
        }));
      } else {
        localStorage.removeItem('etch.persona');
      }
    } catch {}
  }, []);

  // Restore persona selection from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('etch.persona');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.circleId || parsed?.personaId) {
          setSelectedPersonaState(parsed);
          // The choice outlives a login and may come from another tenant's
          // circle, which Persona Studio refuses (403). A failed list keeps it.
          apiClient.listPersonas()
            .then(({ circles }) => {
              if (!isPersonaAvailable(parsed, circles)) setSelectedPersona(null);
            })
            .catch(() => {});
        }
      }
    } catch {}
  }, [setSelectedPersona]);

  const settings = useSettings();
  const { i18n } = useTranslation();

  // Try to restore session from sessionStorage on mount
  useEffect(() => {
    let ignore = false;

    const restoreSession = async () => {
      try {
        const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!stored) {
          if (!ignore) setIsRestoring(false);
          return;
        }

        const { sessionId: storedId, workspacePath: storedPath, mode: storedMode } = JSON.parse(stored);
        if (!storedId || !storedPath) {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
          if (!ignore) setIsRestoring(false);
          return;
        }

        // Validate session still exists on server
        if (!ignore) setStatus('connecting');
        const session = await apiClient.getSession(storedId);

        // Only update state if component is still mounted
        if (!ignore) {
          setSessionId(session.sessionId);
          setCodebaseContext(session.codebaseContext);
          setWorkspacePath(storedPath);
          setCkbConnected(session.ckbConnected ?? false);
          setCkbToolCount(session.ckbToolCount ?? 0);
          setSessionProvider(session.provider);
          setSessionModel(session.model);
          setSessionMode(storedMode || session.mode || 'codebase');
          setStatus('connected');
        }
      } catch {
        // Session no longer valid, clear storage
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        if (!ignore) setStatus('disconnected');
      } finally {
        if (!ignore) setIsRestoring(false);
      }
    };

    restoreSession();

    return () => {
      ignore = true;
    };
  }, []);

  const createSession = useCallback(async (path, options = {}) => {
    setStatus('connecting');
    setError(null);

    const mode = options.mode || 'codebase';

    try {
      const session = await apiClient.createSession(path, {
        // Sent only once the user actually chose a provider (SettingsContext:
        // providerTouched) — an omitted `provider` lets the server fall back
        // to the tenant LLM policy's own `defaultProvider` (routes/api.js),
        // which a hardcoded 'gemini' default here would otherwise defeat for
        // every member who never opened Settings (nyxCore action point a10ba442).
        ...(settings.providerTouched ? { provider: settings.provider, model: settings.model } : {}),
        ollamaBaseUrl: settings.ollamaBaseUrl,
        mode,
        language: i18n.language
      });
      setSessionId(session.sessionId);
      setCodebaseContext(session.codebaseContext);
      setWorkspacePath(path || null);
      setCkbConnected(session.ckbConnected ?? false);
      setCkbToolCount(session.ckbToolCount ?? 0);
      setSessionProvider(session.provider);
      setSessionModel(session.model);
      setSessionMode(mode);
      setStatus('connected');

      // Persist session to sessionStorage for restoration on refresh
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
        sessionId: session.sessionId,
        workspacePath: path || null,
        mode
      }));

      return session;
    } catch (err) {
      setError(err.message);
      setStatus('error');
      throw err;
    }
  }, [settings.provider, settings.model, settings.providerTouched, settings.ollamaBaseUrl]);

  const destroySession = useCallback(async () => {
    if (sessionId) {
      try {
        await apiClient.deleteSession(sessionId);
      } catch (err) {
        console.error('Failed to destroy session:', err);
      }
    }
    // Clear persisted session
    sessionStorage.removeItem(SESSION_STORAGE_KEY);

    setSessionId(null);
    setStatus('disconnected');
    setCodebaseContext(null);
    setWorkspacePath(null);
    setCkbConnected(false);
    setCkbToolCount(0);
    setSessionProvider(null);
    setSessionModel(null);
    setSessionMode('codebase');
    setError(null);
  }, [sessionId]);

  const clearHistory = useCallback(async () => {
    if (sessionId) {
      await apiClient.clearHistory(sessionId);
    }
  }, [sessionId]);

  const value = {
    sessionId,
    status,
    codebaseContext,
    workspacePath,
    ckbConnected,
    ckbToolCount,
    error,
    sessionProvider,
    sessionModel,
    sessionMode,
    selectedPersona,
    setSelectedPersona,
    isConnected: status === 'connected',
    isConnecting: status === 'connecting',
    isRestoring, // True while checking for stored session on mount
    createSession,
    destroySession,
    clearHistory
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

export default SessionContext;
